import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Textarea,
  Badge,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useInviteMembers } from '@/api/teams';

interface InviteMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a free-text blob on commas, spaces, semicolons and newlines. */
function parseEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

export function InviteMembersDialog({ open, onOpenChange, teamId }: InviteMembersDialogProps) {
  const { show } = useToast();
  const [raw, setRaw] = useState('');

  const { valid, invalid } = useMemo(() => {
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const e of parseEmails(raw)) {
      if (seen.has(e)) continue;
      seen.add(e);
      (EMAIL_RE.test(e) ? valid : invalid).push(e);
    }
    return { valid, invalid };
  }, [raw]);

  const invite = useInviteMembers(teamId, {
    onSuccess: () => {
      show({
        tone: 'success',
        title: `Invited ${valid.length} member${valid.length === 1 ? '' : 's'}`,
      });
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not send invitations' }),
  });

  function handleOpenChange(next: boolean) {
    if (!next) setRaw('');
    onOpenChange(next);
  }

  const canSubmit = valid.length > 0 && invalid.length === 0 && !invite.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Invite members</DialogTitle>
          <DialogDescription>
            They'll be added as pending members and emailed an invitation.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 12 }}>
            <Field
              label="Email addresses"
              description="Separate multiple emails with a comma, space, or new line."
            >
              {(props) => (
                <Textarea
                  {...props}
                  autoFocus
                  rows={3}
                  placeholder="alice@example.com, bob@example.com"
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                />
              )}
            </Field>

            {valid.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {valid.map((e) => (
                  <Badge key={e} tone="primary" appearance="subtle" size="sm">
                    {e}
                  </Badge>
                ))}
              </div>
            )}

            {invalid.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  alignItems: 'center',
                  fontSize: 13,
                  color: 'var(--vn-danger-text)',
                }}
              >
                <X size={14} />
                <span>Invalid:</span>
                {invalid.map((e) => (
                  <Badge key={e} tone="danger" appearance="subtle" size="sm">
                    {e}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => invite.mutate({ emails: valid, roles: ['member'] })}
          >
            {invite.isPending
              ? 'Sending…'
              : `Send invitation${valid.length === 1 ? '' : 's'}${valid.length ? ` (${valid.length})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
